import { clamp, lastDefined } from "../utils/formatters.js";

function valueOf(point) {
  if (typeof point === "number") return point;
  if (!point || typeof point !== "object") return null;
  const value = point.close ?? point.price ?? point.value;
  return Number.isFinite(Number(value)) ? Number(value) : null;
}

function series(data) {
  return Array.isArray(data) ? data.map(valueOf) : [];
}

function mean(values) {
  const valid = values.filter((value) => Number.isFinite(Number(value))).map(Number);
  if (!valid.length) return null;
  return valid.reduce((sum, value) => sum + value, 0) / valid.length;
}

function standardDeviation(values) {
  const avg = mean(values);
  if (avg === null) return null;
  const variance = values.reduce((sum, value) => sum + (Number(value) - avg) ** 2, 0) / values.length;
  return Math.sqrt(variance);
}

export function calculateSMA(data, period = 20) {
  const values = series(data);
  if (!values.length || period <= 0) return [];
  return values.map((_, index) => {
    if (index + 1 < period) return null;
    const window = values.slice(index + 1 - period, index + 1);
    if (window.some((value) => !Number.isFinite(value))) return null;
    return mean(window);
  });
}

export function calculateEMA(data, period = 20) {
  const values = series(data);
  if (!values.length || period <= 0) return [];
  const multiplier = 2 / (period + 1);
  const output = Array(values.length).fill(null);
  let previousEma = null;

  values.forEach((value, index) => {
    if (!Number.isFinite(value)) return;
    if (index + 1 === period) {
      const firstWindow = values.slice(0, period);
      if (firstWindow.every(Number.isFinite)) {
        previousEma = mean(firstWindow);
        output[index] = previousEma;
      }
      return;
    }
    if (index + 1 > period && previousEma !== null) {
      previousEma = value * multiplier + previousEma * (1 - multiplier);
      output[index] = previousEma;
    }
  });

  return output;
}

export function calculateRSI(data, period = 14) {
  const values = series(data);
  if (values.length < period + 1 || period <= 0) return values.map(() => null);
  const output = Array(values.length).fill(null);
  let gains = 0;
  let losses = 0;

  for (let index = 1; index <= period; index += 1) {
    const change = values[index] - values[index - 1];
    if (change >= 0) gains += change;
    else losses += Math.abs(change);
  }

  let avgGain = gains / period;
  let avgLoss = losses / period;
  output[period] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);

  for (let index = period + 1; index < values.length; index += 1) {
    const change = values[index] - values[index - 1];
    const gain = change > 0 ? change : 0;
    const loss = change < 0 ? Math.abs(change) : 0;
    avgGain = (avgGain * (period - 1) + gain) / period;
    avgLoss = (avgLoss * (period - 1) + loss) / period;
    output[index] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
  }

  return output;
}

export function calculateMACD(data, fastPeriod = 12, slowPeriod = 26, signalPeriod = 9) {
  const fast = calculateEMA(data, fastPeriod);
  const slow = calculateEMA(data, slowPeriod);
  const macdLine = fast.map((fastValue, index) => {
    const slowValue = slow[index];
    return Number.isFinite(fastValue) && Number.isFinite(slowValue) ? fastValue - slowValue : null;
  });
  const compactMacd = macdLine.filter((value) => Number.isFinite(value));
  const compactSignal = calculateEMA(compactMacd, signalPeriod);
  let signalIndex = 0;
  const signalLine = macdLine.map((value) => {
    if (!Number.isFinite(value)) return null;
    const signal = compactSignal[signalIndex] ?? null;
    signalIndex += 1;
    return signal;
  });
  const histogram = macdLine.map((value, index) => {
    const signal = signalLine[index];
    return Number.isFinite(value) && Number.isFinite(signal) ? value - signal : null;
  });

  return macdLine.map((value, index) => ({
    macdLine: value,
    signalLine: signalLine[index],
    histogram: histogram[index],
  }));
}

export function calculateBollingerBands(data, period = 20, multiplier = 2) {
  const values = series(data);
  const middle = calculateSMA(values, period);
  const upper = Array(values.length).fill(null);
  const lower = Array(values.length).fill(null);

  values.forEach((_, index) => {
    if (index + 1 < period) return;
    const window = values.slice(index + 1 - period, index + 1);
    if (window.some((value) => !Number.isFinite(value))) return;
    const std = standardDeviation(window);
    if (std === null || middle[index] === null) return;
    upper[index] = middle[index] + std * multiplier;
    lower[index] = middle[index] - std * multiplier;
  });

  return middle.map((value, index) => ({
    middle: value,
    upper: upper[index],
    lower: lower[index],
  }));
}

export function calculateATR(data, period = 14) {
  if (!Array.isArray(data) || !data.length || period <= 0) return [];
  const trueRanges = data.map((point, index) => {
    const high = Number(point?.high ?? point?.price ?? point?.close);
    const low = Number(point?.low ?? point?.price ?? point?.close);
    const previousClose = index > 0 ? Number(data[index - 1]?.close ?? data[index - 1]?.price) : high;
    if (![high, low, previousClose].every(Number.isFinite)) return null;
    return Math.max(high - low, Math.abs(high - previousClose), Math.abs(low - previousClose));
  });

  const output = Array(trueRanges.length).fill(null);
  for (let index = period - 1; index < trueRanges.length; index += 1) {
    const window = trueRanges.slice(index + 1 - period, index + 1);
    if (window.some((value) => !Number.isFinite(value))) continue;
    output[index] = mean(window);
  }
  return output;
}

export function detectSupportResistance(data, lookback = 60) {
  if (!Array.isArray(data) || !data.length) {
    return { support: null, resistance: null };
  }
  const window = data.slice(-lookback);
  const lows = window.map((point) => Number(point?.low ?? point?.price ?? point?.close)).filter(Number.isFinite);
  const highs = window.map((point) => Number(point?.high ?? point?.price ?? point?.close)).filter(Number.isFinite);
  if (!lows.length || !highs.length) return { support: null, resistance: null };
  return {
    support: Math.min(...lows),
    resistance: Math.max(...highs),
  };
}

export function enrichHistoryWithIndicators(data = []) {
  const safeData = Array.isArray(data) ? data : [];
  const sma20 = calculateSMA(safeData, 20);
  const sma50 = calculateSMA(safeData, 50);
  const ema9 = calculateEMA(safeData, 9);
  const ema21 = calculateEMA(safeData, 21);
  const ema50 = calculateEMA(safeData, 50);
  const rsi14 = calculateRSI(safeData, 14);
  const macd = calculateMACD(safeData);
  const bollinger = calculateBollingerBands(safeData, 20, 2);
  const atr14 = calculateATR(safeData, 14);
  const levels = detectSupportResistance(safeData);

  return safeData.map((point, index) => ({
    ...point,
    sma20: sma20[index],
    sma50: sma50[index],
    ema9: ema9[index],
    ema21: ema21[index],
    ema50: ema50[index],
    rsi14: rsi14[index],
    macdLine: macd[index]?.macdLine ?? null,
    signalLine: macd[index]?.signalLine ?? null,
    macdHistogram: macd[index]?.histogram ?? null,
    bollingerMiddle: bollinger[index]?.middle ?? null,
    bollingerUpper: bollinger[index]?.upper ?? null,
    bollingerLower: bollinger[index]?.lower ?? null,
    atr14: atr14[index],
    support: levels.support,
    resistance: levels.resistance,
  }));
}

export function getLatestIndicators(data = []) {
  const enriched = enrichHistoryWithIndicators(data);
  const latest = enriched[enriched.length - 1] ?? {};
  const supportResistance = detectSupportResistance(enriched);
  const latestPrice = Number(latest.close ?? latest.price);
  const ema21 = lastDefined(enriched.map((point) => point.ema21));
  const ema50 = lastDefined(enriched.map((point) => point.ema50));
  let trend = "sideways";
  if (Number.isFinite(latestPrice) && Number.isFinite(ema21) && Number.isFinite(ema50)) {
    if (latestPrice > ema21 && ema21 > ema50) trend = "bullish";
    if (latestPrice < ema21 && ema21 < ema50) trend = "bearish";
  }

  return {
    price: latestPrice,
    sma20: lastDefined(enriched.map((point) => point.sma20)),
    sma50: lastDefined(enriched.map((point) => point.sma50)),
    ema9: lastDefined(enriched.map((point) => point.ema9)),
    ema21,
    ema50,
    rsi14: lastDefined(enriched.map((point) => point.rsi14)),
    macdLine: lastDefined(enriched.map((point) => point.macdLine)),
    signalLine: lastDefined(enriched.map((point) => point.signalLine)),
    macdHistogram: lastDefined(enriched.map((point) => point.macdHistogram)),
    bollingerMiddle: lastDefined(enriched.map((point) => point.bollingerMiddle)),
    bollingerUpper: lastDefined(enriched.map((point) => point.bollingerUpper)),
    bollingerLower: lastDefined(enriched.map((point) => point.bollingerLower)),
    atr14: lastDefined(enriched.map((point) => point.atr14)),
    support: supportResistance.support,
    resistance: supportResistance.resistance,
    volume: latest.volume ?? null,
    trend,
  };
}

export function calculateTechnicalScore(indicators = {}) {
  let score = 50;
  const reasons = [];
  const warnings = [];
  const price = Number(indicators.price);
  const ema9 = Number(indicators.ema9);
  const ema21 = Number(indicators.ema21);
  const ema50 = Number(indicators.ema50);
  const rsi = Number(indicators.rsi14);
  const macd = Number(indicators.macdLine);
  const signal = Number(indicators.signalLine);
  const support = Number(indicators.support);
  const resistance = Number(indicators.resistance);

  if (Number.isFinite(price) && Number.isFinite(ema21)) {
    if (price > ema21) {
      score += 12;
      reasons.push("Золото торгуется выше EMA 21");
    } else {
      score -= 12;
      warnings.push("Золото торгуется ниже EMA 21");
    }
  }

  if (Number.isFinite(ema9) && Number.isFinite(ema21)) {
    if (ema9 > ema21) {
      score += 10;
      reasons.push("EMA 9 выше EMA 21");
    } else {
      score -= 10;
      warnings.push("EMA 9 ниже EMA 21");
    }
  }

  if (Number.isFinite(ema21) && Number.isFinite(ema50)) {
    if (ema21 > ema50) score += 6;
    if (ema21 < ema50) score -= 6;
  }

  if (Number.isFinite(macd) && Number.isFinite(signal)) {
    if (macd > signal) {
      score += 10;
      reasons.push("MACD подтверждает моментум");
    } else {
      score -= 10;
      warnings.push("MACD показывает ослабление моментума");
    }
  }

  if (Number.isFinite(rsi)) {
    if (rsi >= 45 && rsi <= 68) {
      score += 8;
      reasons.push("RSI находится в конструктивной зоне");
    }
    if (rsi > 75) {
      score -= 14;
      warnings.push("RSI сильно перекуплен");
    } else if (rsi > 68) {
      score -= 6;
      warnings.push("RSI близок к перекупленности");
    }
    if (rsi < 30) {
      score -= 4;
      warnings.push("RSI перепродан, возможна волатильность");
    }
  }

  if (Number.isFinite(price) && Number.isFinite(support) && support > 0) {
    const distanceToSupport = ((price - support) / support) * 100;
    if (distanceToSupport > 0.5) score += 4;
    if (distanceToSupport < 1.5) warnings.push("Цена находится рядом с поддержкой");
  }

  if (Number.isFinite(price) && Number.isFinite(resistance) && resistance > 0) {
    const distanceToResistance = ((resistance - price) / resistance) * 100;
    if (distanceToResistance < 1.5) {
      score -= 5;
      warnings.push("Цена находится рядом с сопротивлением");
    }
  }

  if (indicators.trend === "bullish") score += 6;
  if (indicators.trend === "bearish") score -= 6;

  return {
    score: Math.round(clamp(score)),
    reasons,
    warnings,
    trend: indicators.trend ?? "sideways",
  };
}
